/**
 * Content Tab
 *
 * Composite page that merges the dashboard welcome/stats section
 * with the full training list into a single scrollable view.
 * Absorbs logic from OperatorDashboardPage + OperatorTrainingPage.
 *
 * Sections (top to bottom):
 * 1. Compact greeting header with inline stats
 * 2. Preferences card (conditional, deferred)
 * 3. "For You" spotlight (top recommended items, dynamic, capped at 3)
 * 4. All Complete celebration (slim banner, if all done)
 * 5. Post-completion "Up Next" nudge (if justCompleted param)
 * 6. Training list: header, filter pills, search + sort, cards
 *
 * Container is provided by OperatorLayout -- no wrapper needed.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Inbox,
  Search,
  ArrowUpDown,
  Check,
  Sparkles,
  ArrowRight,
  PartyPopper,
  FileText,
  Brain,
} from 'lucide-react';
import { useOperatorDashboard, useOperatorTraining, useOperatorSops } from '../../hooks/useOperatorPortal';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { OperatorLoadingSkeleton } from './components/OperatorLoadingSkeleton';
import { OperatorEmptyState } from './components/OperatorEmptyState';
import { OperatorCard } from './components/OperatorCard';
import { TrainingCard } from './components/TrainingCard';
import { BundleCard } from './components/BundleCard';
import { SpotlightCard } from './components/SpotlightCard';
import { PracticeCard } from './components/PracticeCard';
import { ContextualTooltip } from './components/ContextualTooltip';
import { PreferencesCard } from './components/PreferencesCard';
import { PreferencesSheet } from './components/PreferencesSheet';
import { BottomSheet } from './components/BottomSheet';
import { usePreferences } from './hooks/usePreferences';
import { useAvailablePractice } from './hooks/useOperatorPractice';
import { cn } from '../../lib/utils';
import { formatRelativeDate } from './utils';

type StatusFilter = 'all' | 'assigned' | 'in_progress' | 'completed' | 'overdue';
type SortOption = 'recommended' | 'due_date' | 'title' | 'progress';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'due_date', label: 'Due Date' },
  { value: 'title', label: 'Title' },
  { value: 'progress', label: 'Progress' },
];

/** Maximum number of items to show in the "For You" spotlight */
const MAX_SPOTLIGHT_ITEMS = 3;

/** Maximum number of practice items to show in the Knowledge Checks section */
const MAX_PRACTICE_ITEMS = 4;

export function ContentTab() {
  useDocumentTitle('Content');

  const { data: dashboard, isLoading: dashboardLoading } = useOperatorDashboard();
  const { data: trainingItems, isLoading: trainingLoading } = useOperatorTraining();
  const { data: sops } = useOperatorSops();
  const { data: practiceData } = useAvailablePractice();

  const isLoading = dashboardLoading || trainingLoading;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Track justCompleted in local state so the nudge persists visually,
  // while the URL param is consumed immediately to prevent stale nudges on refresh.
  const [showUpNext, setShowUpNext] = useState(false);

  useEffect(() => {
    const completed = searchParams.get('justCompleted') === 'true';
    if (completed) {
      setShowUpNext(true);
      // Clear the URL param so refresh doesn't re-show the nudge
      navigate('/operator/content', { replace: true });
    }
  }, [searchParams, navigate]);

  // Refs for contextual tooltips
  const filterPillsRef = useRef<HTMLDivElement>(null);

  // Preferences card state
  const hasCompletedTraining = trainingItems?.some(item => item.status === 'completed') ?? false;
  const sopNames = [...new Set(
    (trainingItems ?? [])
      .map(item => item.sopName)
      .filter((name): name is string => name !== null && name !== undefined)
  )];
  const { shouldShowCard, snooze, savePreferences } = usePreferences(hasCompletedTraining);
  const [prefsSheetOpen, setPrefsSheetOpen] = useState(false);

  // Training list state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('recommended');
  const [sortSheetOpen, setSortSheetOpen] = useState(false);

  // Compute spotlight items: in-progress first, then overdue+recommended, then top by relevanceScore
  const spotlightItems = useMemo(() => {
    if (!trainingItems || trainingItems.length === 0) return [];

    // Exclude quiz bundle slots — they are navigated via BundleCard, not the spotlight.
    // A quiz row with status='in_progress' would otherwise surface here with an
    // incorrect "Document" label and loop back to /operator/content on click.
    const incomplete = trainingItems.filter(i => i.status !== 'completed' && i.bundleSlot !== 'quiz');
    if (incomplete.length === 0) return [];

    const inProgress = incomplete.filter(i => i.status === 'in_progress');
    const overdueRecommended = incomplete.filter(
      i => i.status !== 'in_progress' && i.isOverdue && i.isRecommended
    );
    const recommended = incomplete.filter(
      i => i.status !== 'in_progress' && !i.isOverdue && i.isRecommended
    );
    const rest = incomplete
      .filter(i => i.status !== 'in_progress' && !i.isRecommended)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    // Build priority queue: in-progress -> overdue+recommended -> recommended -> high relevance
    // Filter to only meaningful items first, then cap — ensures we get up to 3 qualifying items
    const candidates = [...inProgress, ...overdueRecommended, ...recommended, ...rest]
      .filter(i => i.status === 'in_progress' || i.isRecommended || i.isOverdue);

    // Deduplicate (in case of overlap) and cap at MAX_SPOTLIGHT_ITEMS
    const seen = new Set<string>();
    const result = [];
    for (const item of candidates) {
      if (seen.has(item.assignmentId)) continue;
      seen.add(item.assignmentId);
      result.push(item);
      if (result.length >= MAX_SPOTLIGHT_ITEMS) break;
    }

    return result;
  }, [trainingItems]);

  // "Up Next" nudge item (shown after completing a training item)
  const upNextItem = useMemo(() => {
    if (!showUpNext || !trainingItems) return null;

    // Find the top recommended incomplete item. Exclude quiz bundle slots for the
    // same reason as the spotlight above — the nudge has no playable viewer for a
    // quiz row and the link target would loop back to /operator/content.
    const incomplete = trainingItems
      .filter(i => i.status !== 'completed' && i.bundleSlot !== 'quiz')
      .sort((a, b) => {
        // In-progress first
        if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
        if (b.status === 'in_progress' && a.status !== 'in_progress') return 1;
        // Then by relevance
        return b.relevanceScore - a.relevanceScore;
      });

    return incomplete[0] ?? null;
  }, [showUpNext, trainingItems]);

  // Practice items: combine quizzes and sequencers, prioritize untried > lowest score > most recent
  const practiceItems = useMemo(() => {
    if (!practiceData) return [];

    type PracticeItem = {
      type: 'quiz' | 'sequencer';
      targetId: string;
      title: string;
      sopTitle: string;
      itemCount: number;
      bestScore: number | null;
      attemptCount: number;
    };

    const items: PracticeItem[] = [];

    for (const q of practiceData.quizzes ?? []) {
      items.push({
        type: 'quiz',
        targetId: q.sectionId,
        title: q.sectionTitle,
        sopTitle: q.sopTitle,
        itemCount: q.questionCount,
        bestScore: q.bestScore,
        attemptCount: q.attemptCount,
      });
    }

    for (const s of practiceData.sequencers ?? []) {
      items.push({
        type: 'sequencer',
        targetId: s.taskId,
        title: s.taskTitle,
        sopTitle: s.sopTitle,
        itemCount: s.stepCount,
        bestScore: s.bestScore,
        attemptCount: s.attemptCount,
      });
    }

    // Sort: untried first, then lowest bestScore, then alphabetically
    items.sort((a, b) => {
      const aUntried = a.attemptCount === 0 ? 0 : 1;
      const bUntried = b.attemptCount === 0 ? 0 : 1;
      if (aUntried !== bUntried) return aUntried - bUntried;
      const aScore = a.bestScore ?? -1;
      const bScore = b.bestScore ?? -1;
      if (aScore !== bScore) return aScore - bScore;
      return a.title.localeCompare(b.title);
    });

    return items.slice(0, MAX_PRACTICE_ITEMS);
  }, [practiceData]);

  // Quick Check nudge: after training completion, suggest a quiz for the same SOP section
  const quickCheckQuiz = useMemo(() => {
    if (!showUpNext || !practiceData?.quizzes?.length) return null;

    // Find a quiz section that the operator hasn't tried yet (or has lowest score)
    // The "Quick Check" is shown after any training completion, suggest first available quiz
    const untried = practiceData.quizzes.find((q) => q.attemptCount === 0);
    return untried ?? practiceData.quizzes[0] ?? null;
  }, [showUpNext, practiceData]);

  // Filter and sort training items
  const filteredItems = useMemo(() => {
    if (!trainingItems) return [];

    let items = [...trainingItems];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.material.title.toLowerCase().includes(query) ||
          item.material.description?.toLowerCase().includes(query)
      );
    }

    // Status filter
    switch (statusFilter) {
      case 'overdue':
        items = items.filter((item) => item.isOverdue && item.status !== 'completed');
        break;
      case 'all':
        break;
      default:
        items = items.filter((item) => item.status === statusFilter);
        break;
    }

    // Sort
    items.sort((a, b) => {
      switch (sortBy) {
        case 'recommended': {
          const aOverdue = a.isOverdue && a.status !== 'completed' ? 1 : 0;
          const bOverdue = b.isOverdue && b.status !== 'completed' ? 1 : 0;
          if (aOverdue !== bOverdue) return bOverdue - aOverdue;
          return b.relevanceScore - a.relevanceScore;
        }
        case 'due_date':
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        case 'title':
          return a.material.title.localeCompare(b.material.title);
        case 'progress':
          return b.progress - a.progress;
        default:
          return 0;
      }
    });

    return items;
  }, [trainingItems, searchQuery, statusFilter, sortBy]);

  // Stats for quick filter pills
  const filterStats = useMemo(() => {
    if (!trainingItems)
      return { total: 0, assigned: 0, inProgress: 0, completed: 0, overdue: 0 };

    return {
      total: trainingItems.length,
      assigned: trainingItems.filter((i) => i.status === 'assigned').length,
      inProgress: trainingItems.filter((i) => i.status === 'in_progress').length,
      completed: trainingItems.filter((i) => i.status === 'completed').length,
      overdue: trainingItems.filter((i) => i.isOverdue && i.status !== 'completed').length,
    };
  }, [trainingItems]);

  // --- Loading ---
  if (isLoading) {
    return <OperatorLoadingSkeleton variant="page" />;
  }

  // --- No operator profile ---
  if (!dashboard?.operator) {
    return (
      <OperatorEmptyState
        icon={Inbox}
        title="No Operator Profile Found"
        description={dashboard?.message || "Your account isn't linked to an operator profile. Please contact your manager."}
      />
    );
  }

  const { operator, stats } = dashboard;
  const allComplete = stats.completedAssignments === stats.totalAssignments && stats.totalAssignments > 0;

  const pillClasses = (active: boolean) =>
    cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] font-medium min-h-[36px] transition-colors duration-150',
      'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
      active
        ? 'bg-guild-green text-white'
        : 'bg-op-muted text-op-text-secondary active:bg-gray-200',
    );

  return (
    <div className="space-y-5">
      {/* ============================================ */}
      {/* SECTION 1: Compact Greeting Header           */}
      {/* ============================================ */}
      <div
        className="flex items-center justify-between gap-3"
        style={{ animation: 'fadeInUp 0.4s ease-out' }}
      >
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-semibold text-op-text-primary">
            Hi, {operator.firstName}
          </h1>
          <p className="mt-0.5 text-sm text-op-text-secondary">
            {stats.completedAssignments} of {stats.totalAssignments} training complete
            {stats.overdueAssignments > 0 && (
              <span className="text-red-600 font-medium">
                {' '}&middot; {stats.overdueAssignments} overdue
              </span>
            )}
          </p>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 2: Preferences Card (conditional)   */}
      {/* Hidden when operator has completed onboarding */}
      {/* ============================================ */}
      {shouldShowCard && !dashboard?.hasCompletedOnboarding && (
        <PreferencesCard
          onPersonalize={() => setPrefsSheetOpen(true)}
          onLater={snooze}
        />
      )}

      {/* ============================================ */}
      {/* SECTION 3: Post-completion "Up Next" nudge  */}
      {/* ============================================ */}
      {showUpNext && upNextItem && (
        <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
          <OperatorCard
            href={`/operator/content/${upNextItem.assignmentId}`}
            className="overflow-hidden border-l-4 border-emerald-500 !rounded-l-lg"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                <CheckCircle2 size={22} strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-emerald-600">Nice work! Up next:</p>
                <p className="text-sm font-semibold text-op-text-primary line-clamp-1">
                  {upNextItem.material.title}
                </p>
                <p className="text-xs text-op-text-tertiary">
                  {upNextItem.status === 'in_progress'
                    ? `${upNextItem.progress}% complete`
                    : 'Ready to start'}
                </p>
              </div>
              <ArrowRight size={18} strokeWidth={1.5} className="flex-shrink-0 text-op-text-tertiary" />
            </div>
          </OperatorCard>
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION 3b: Quick Check nudge (post-training) */}
      {/* ============================================ */}
      {showUpNext && quickCheckQuiz && (
        <div style={{ animation: 'fadeInUp 0.4s ease-out 0.06s both' }}>
          <OperatorCard
            href={`/operator/practice/quiz/${quickCheckQuiz.sectionId}`}
            className="overflow-hidden border-l-4 border-cyan-500 !rounded-l-lg"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-cyan-100 text-cyan-600">
                <Brain size={22} strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-cyan-600">Quick Check</p>
                <p className="text-sm font-semibold text-op-text-primary line-clamp-1">
                  Test what you learned &mdash; {quickCheckQuiz.sectionTitle}
                </p>
                <p className="text-xs text-op-text-tertiary">
                  {quickCheckQuiz.questionCount} questions &middot; ~2 min
                </p>
              </div>
              <ArrowRight size={18} strokeWidth={1.5} className="flex-shrink-0 text-op-text-tertiary" />
            </div>
          </OperatorCard>
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION 4: "For You" Spotlight               */}
      {/* ============================================ */}
      {spotlightItems.length > 0 && !allComplete && (
        <div style={{ animation: 'fadeInUp 0.4s ease-out 0.06s both' }}>
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={16} strokeWidth={1.5} className="text-guild-green" />
            <h2 className="text-base font-semibold text-op-text-primary">For You</h2>
          </div>
          <div className="flex flex-col gap-3">
            {spotlightItems.map((item, index) => (
              <SpotlightCard key={item.assignmentId} item={item} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION 4b: Knowledge Checks                */}
      {/* ============================================ */}
      {practiceItems.length > 0 && (
        <div style={{ animation: 'fadeInUp 0.4s ease-out 0.09s both' }}>
          <div className="mb-3 flex items-center gap-2">
            <Brain size={16} strokeWidth={1.5} className="text-cyan-600" />
            <h2 className="text-base font-semibold text-op-text-primary">Knowledge Checks</h2>
          </div>
          <div className="flex flex-col gap-3">
            {practiceItems.map((item, index) => (
              <PracticeCard
                key={`${item.type}-${item.targetId}`}
                type={item.type}
                targetId={item.targetId}
                title={item.title}
                sopTitle={item.sopTitle}
                itemCount={item.itemCount}
                bestScore={item.bestScore}
                attemptCount={item.attemptCount}
                index={index}
              />
            ))}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION 5: All Complete Celebration (slim)   */}
      {/* ============================================ */}
      {allComplete && (
        <div
          className="flex items-center gap-3 rounded-2xl bg-emerald-50 px-4 py-3 ring-1 ring-emerald-200"
          style={{ animation: 'fadeInUp 0.4s ease-out 0.06s both' }}
        >
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
            <PartyPopper size={20} strokeWidth={1.5} className="text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-emerald-800">All Training Complete!</p>
            <p className="text-xs text-emerald-600">
              Congratulations! You've completed all your assigned training.
            </p>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION 6: Training List                     */}
      {/* ============================================ */}

      {/* No training at all -- show empty state instead of list UI */}
      {stats.totalAssignments === 0 ? (
        <OperatorEmptyState
          icon={BookOpen}
          title="No Training Assigned"
          description="You don't have any training materials assigned yet. Check back later."
        />
      ) : (
        <>
          {/* Training header */}
          <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
            <h2 className="text-lg font-semibold text-op-text-primary">My Training</h2>
            <p className="mt-0.5 text-sm text-op-text-secondary">
              {filterStats.completed} of {filterStats.total} complete
            </p>
          </div>

          {/* Status filter pills -- horizontal scroll */}
          <div
            ref={filterPillsRef}
            className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide"
            style={{ animation: 'fadeInUp 0.4s ease-out 0.06s both' }}
          >
            <button
              onClick={() => setStatusFilter('all')}
              className={pillClasses(statusFilter === 'all')}
            >
              <BookOpen size={14} strokeWidth={1.5} />
              All ({filterStats.total})
            </button>
            <button
              onClick={() => setStatusFilter('in_progress')}
              className={pillClasses(statusFilter === 'in_progress')}
            >
              <Clock size={14} strokeWidth={1.5} />
              In Progress ({filterStats.inProgress})
            </button>
            <button
              onClick={() => setStatusFilter('assigned')}
              className={pillClasses(statusFilter === 'assigned')}
            >
              <Inbox size={14} strokeWidth={1.5} />
              Not Started ({filterStats.assigned})
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={pillClasses(statusFilter === 'completed')}
            >
              <CheckCircle2 size={14} strokeWidth={1.5} />
              Completed ({filterStats.completed})
            </button>
            {filterStats.overdue > 0 && (
              <button
                onClick={() => setStatusFilter('overdue')}
                className={pillClasses(statusFilter === 'overdue')}
              >
                <AlertTriangle size={14} strokeWidth={1.5} />
                {filterStats.overdue} Overdue
              </button>
            )}
          </div>

          <ContextualTooltip
            tooltipId="training-filters"
            targetRef={filterPillsRef}
            message="Filter by status to find specific training."
            position="below"
          />

          {/* Search and Sort */}
          <div
            className="flex items-center gap-2"
            style={{ animation: 'fadeInUp 0.4s ease-out 0.12s both' }}
          >
            <div className="relative flex-1">
              <Search
                size={18}
                strokeWidth={1.5}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-op-text-tertiary"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search training..."
                aria-label="Search training"
                className="w-full rounded-xl border-0 bg-op-muted py-2.5 pl-10 pr-4 text-sm text-op-text-primary placeholder:text-op-text-tertiary focus:outline-none focus:ring-2 focus:ring-guild-green/30"
              />
            </div>

            <button
              type="button"
              onClick={() => setSortSheetOpen(true)}
              className="flex items-center gap-1.5 rounded-xl bg-op-muted px-3.5 py-2.5 text-[13px] font-medium text-op-text-secondary active:bg-gray-200 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            >
              <ArrowUpDown size={14} strokeWidth={1.5} />
              Sort
            </button>
          </div>

          {/* Training card list
              Phase 25.4: Split filtered items into Personal (tier=1) and Team
              (tier=2) groups. Within Personal, cluster items that share a
              `fanoutGroupId` (>=2 rows) into a single BundleCard. The render
              rule is based on row count at runtime — NOT the
              `RING_1_INCLUDES_QUIZ` config flag — so a config flip during an
              active rollout still renders bundled rows correctly as a bundle
              and non-bundled rows as standalone cards. */}
          {filteredItems.length > 0 ? (
            (() => {
              // Split by tier — Ring 1 and legacy (tier=1 or null) go to
              // Personal; Ring 2 refresher/focused_drill (tier=2) go to Team.
              const personalItems = filteredItems.filter((i) => i.tier !== 2);
              const teamItems = filteredItems.filter((i) => i.tier === 2);

              // Build Ring 1 bundles. Count how many filtered items share
              // each fanoutGroupId — clusters of 2+ get a BundleCard.
              const fgCounts = new Map<string, number>();
              for (const item of personalItems) {
                if (item.fanoutGroupId) {
                  fgCounts.set(
                    item.fanoutGroupId,
                    (fgCounts.get(item.fanoutGroupId) ?? 0) + 1,
                  );
                }
              }

              // Build render order for Personal:
              //  - Recommended first (if sortBy === 'recommended'),
              //    then all other items, each deduped by either
              //    `assignmentId` (standalone) or `fanoutGroupId` (bundle).
              type RenderUnit =
                | { kind: 'single'; key: string; item: typeof personalItems[number] }
                | { kind: 'bundle'; key: string; items: typeof personalItems };

              // Track bundles globally across rec/other groups so the same
              // BundleCard never renders twice. When the rec/other split
              // separates a bundle's video and quiz (their `isRecommended`
              // values can diverge — see operator-portal relevance scoring),
              // the bundle is emitted in the FIRST group whose iteration
              // encounters it. Subsequent encounters are skipped.
              const seenBundleGlobal = new Set<string>();

              const buildUnits = (items: typeof personalItems): RenderUnit[] => {
                const units: RenderUnit[] = [];
                for (const item of items) {
                  if (
                    item.fanoutGroupId &&
                    (fgCounts.get(item.fanoutGroupId) ?? 0) >= 2
                  ) {
                    if (seenBundleGlobal.has(item.fanoutGroupId)) continue;
                    seenBundleGlobal.add(item.fanoutGroupId);
                    const bundleItems = personalItems.filter(
                      (i) => i.fanoutGroupId === item.fanoutGroupId,
                    );
                    units.push({
                      kind: 'bundle',
                      key: `bundle:${item.fanoutGroupId}`,
                      items: bundleItems,
                    });
                  } else {
                    units.push({
                      kind: 'single',
                      key: item.assignmentId,
                      item,
                    });
                  }
                }
                return units;
              };

              const renderUnit = (unit: RenderUnit, index: number) =>
                unit.kind === 'bundle' ? (
                  <BundleCard key={unit.key} items={unit.items} index={index} />
                ) : (
                  <TrainingCard key={unit.key} item={unit.item} index={index} />
                );

              // For Personal, optionally split into Recommended / All when
              // sortBy === 'recommended' (preserves pre-25.4 behavior inside
              // the new grouping). Bundles follow their lead row's
              // recommendation state.
              const personalUnits = (() => {
                if (sortBy !== 'recommended') {
                  return [
                    { label: null, units: buildUnits(personalItems) },
                  ];
                }
                const rec = personalItems.filter((i) => i.isRecommended);
                const other = personalItems.filter((i) => !i.isRecommended);
                return [
                  { label: 'Recommended for You', units: buildUnits(rec) },
                  { label: 'All Personal Training', units: buildUnits(other) },
                ];
              })();

              const personalCount = personalItems.length;
              const teamCount = teamItems.length;

              return (
                <div className="flex flex-col gap-5">
                  {/* Personal group */}
                  <section className="flex flex-col gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-op-text-primary">
                        Your personal trainings
                        <span className="ml-2 rounded-full bg-op-muted px-2 py-0.5 text-[11px] font-semibold text-op-text-secondary">
                          {personalCount}
                        </span>
                      </h3>
                    </div>
                    {personalCount === 0 ? (
                      <OperatorCard className="text-sm text-op-text-secondary">
                        You are all caught up on personal training. Nice work.
                      </OperatorCard>
                    ) : (
                      personalUnits.map((group, gi) => (
                        <div key={gi} className="flex flex-col gap-3">
                          {group.label && group.units.length > 0 && (
                            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-op-text-tertiary">
                              {group.label}
                            </p>
                          )}
                          {group.units.map(renderUnit)}
                        </div>
                      ))
                    )}
                  </section>

                  {/* Team group */}
                  <section className="flex flex-col gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-op-text-primary">
                        Your team refreshers
                        <span className="ml-2 rounded-full bg-op-muted px-2 py-0.5 text-[11px] font-semibold text-op-text-secondary">
                          {teamCount}
                        </span>
                      </h3>
                    </div>
                    {teamCount === 0 ? (
                      <OperatorCard className="text-sm text-op-text-secondary">
                        No team refreshers assigned this week.
                      </OperatorCard>
                    ) : (
                      teamItems.map((item, index) => (
                        <TrainingCard
                          key={item.assignmentId}
                          item={item}
                          index={index}
                        />
                      ))
                    )}
                  </section>
                </div>
              );
            })()
          ) : searchQuery || statusFilter !== 'all' ? (
            <OperatorEmptyState
              icon={Search}
              title={
                searchQuery
                  ? 'No matching training found'
                  : `No ${statusFilter.replace('_', ' ')} training`
              }
              description={
                searchQuery
                  ? 'Try adjusting your search terms'
                  : 'Try selecting a different filter'
              }
              ctaLabel="Clear filters"
              onCtaClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
              }}
            />
          ) : (
            <OperatorEmptyState
              icon={BookOpen}
              title="No Training Assigned"
              description="You don't have any training materials assigned yet."
            />
          )}

          {/* Sort BottomSheet */}
          <BottomSheet
            isOpen={sortSheetOpen}
            onClose={() => setSortSheetOpen(false)}
            title="Sort by"
          >
            <div role="radiogroup" aria-label="Sort options" className="flex flex-col">
              {SORT_OPTIONS.map((option) => {
                const isSelected = sortBy === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => {
                      setSortBy(option.value);
                      setSortSheetOpen(false);
                    }}
                    className={cn(
                      'flex min-h-[48px] items-center justify-between rounded-xl px-3 py-3 text-base transition-colors duration-150',
                      'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
                      isSelected
                        ? 'text-guild-green font-semibold'
                        : 'text-op-text-primary active:bg-gray-100',
                    )}
                  >
                    {option.label}
                    {isSelected && <Check size={18} strokeWidth={2} className="text-guild-green" />}
                  </button>
                );
              })}
            </div>
          </BottomSheet>
        </>
      )}

      {/* ============================================ */}
      {/* SECTION 7: SOP Reference (Phase 21.5.3)     */}
      {/* ============================================ */}
      {sops && sops.length > 0 && (
        <div style={{ animation: 'fadeInUp 0.4s ease-out 0.18s both' }}>
          <div className="mb-3 flex items-center gap-2">
            <FileText size={16} strokeWidth={1.5} className="text-op-text-tertiary" />
            <h2 className="text-base font-semibold text-op-text-primary">SOP Reference</h2>
          </div>
          <p className="mb-3 text-sm text-op-text-secondary">
            Review the procedures you're evaluated on
          </p>
          <div className="flex flex-col gap-2">
            {sops.map((sop) => (
              <OperatorCard key={sop.id} href={`/operator/sops/${sop.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-op-text-primary line-clamp-1">
                      {sop.title}
                    </h3>
                    <p className="text-xs text-op-text-tertiary">
                      {sop.jobCount} {sop.jobCount === 1 ? 'job' : 'jobs'}
                      {sop.lastAssessedDate && ` · Last assessed ${formatRelativeDate(sop.lastAssessedDate)}`}
                    </p>
                  </div>
                  <ArrowRight size={16} strokeWidth={1.5} className="ml-2 flex-shrink-0 text-op-text-tertiary" />
                </div>
              </OperatorCard>
            ))}
          </div>
        </div>
      )}

      {/* Preferences BottomSheet */}
      <PreferencesSheet
        isOpen={prefsSheetOpen}
        onClose={() => setPrefsSheetOpen(false)}
        sopNames={sopNames}
        onSave={(prefs) => {
          savePreferences(prefs);
          setPrefsSheetOpen(false);
        }}
      />
    </div>
  );
}
